import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Movie {
  id: string;
  title: string;
  originalTitle?: string;
  poster: string;
  backdrop?: string;
  year: number;
  rating: number;
  genre: string[];
  description: string;
  sources: MovieSource[];
  sourceId?: string;
  createdAt: string;
}

export interface MovieSource {
  id: string;
  name: string;
  url: string;
  quality: string;
  language: string;
  type?: 'iframe' | 'direct' | 'api';
}

export interface FavoriteItem {
  videoId: string;
  source: string;
  title: string;
  poster: string;
  year: number;
  rating: number;
  genre: string[];
  description: string;
  addedAt: number;
}

export interface HistoryItem {
  videoId: string;
  source: string;
  title: string;
  poster: string;
  year: number;
  rating: number;
  genre: string[];
  description: string;
  sourceId: string;
  progress: number;
  watchedAt: number;
}

interface MovieState {
  movies: Movie[];
  currentMovie: Movie | null;
  searchResults: Movie[];
  loading: boolean;
  error: string | null;
  setMovies: (movies: Movie[]) => void;
  setCurrentMovie: (movie: Movie | null) => void;
  setSearchResults: (results: Movie[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  addMovie: (movie: Movie) => void;
  updateMovie: (id: string, data: Partial<Movie>) => void;
  removeMovie: (id: string) => void;
}

export const useMovieStore = create<MovieState>((set) => ({
  movies: [],
  currentMovie: null,
  searchResults: [],
  loading: false,
  error: null,
  setMovies: (movies) => set({ movies }),
  setCurrentMovie: (movie) => set({ currentMovie: movie }),
  setSearchResults: (results) => set({ searchResults: results }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  addMovie: (movie) => set((state) => ({ movies: [...state.movies, movie] })),
  updateMovie: (id, data) =>
    set((state) => ({
      movies: state.movies.map((m) => (m.id === id ? { ...m, ...data } : m)),
    })),
  removeMovie: (id) =>
    set((state) => ({
      movies: state.movies.filter((m) => m.id !== id),
    })),
}));

const MAX_FAVORITES = 200;
const MAX_HISTORY = 100;

function generateId(videoId: string, source: string): string {
  return `${source}:${videoId}`;
}

interface UserState {
  favorites: FavoriteItem[];
  history: HistoryItem[];
  addFavorite: (item: Omit<FavoriteItem, 'addedAt'>) => void;
  removeFavorite: (videoId: string, source: string) => void;
  toggleFavorite: (item: Omit<FavoriteItem, 'addedAt'>) => boolean;
  isFavorite: (videoId: string, source: string) => boolean;
  addToHistory: (item: Omit<HistoryItem, 'watchedAt'>) => void;
  updateHistoryProgress: (videoId: string, source: string, progress: number) => void;
  removeFromHistory: (videoId: string, source: string) => void;
  clearHistory: () => void;
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      favorites: [],
      history: [],

      addFavorite: (item) => {
        const id = generateId(item.videoId, item.source);
        set((state) => {
          const exists = state.favorites.some(
            (fav) => generateId(fav.videoId, fav.source) === id
          );
          if (exists) return state;

          const newFavorites = [
            { ...item, addedAt: Date.now() },
            ...state.favorites,
          ].slice(0, MAX_FAVORITES);

          return { favorites: newFavorites };
        });
      },

      removeFavorite: (videoId, source) => {
        const id = generateId(videoId, source);
        set((state) => ({
          favorites: state.favorites.filter(
            (fav) => generateId(fav.videoId, fav.source) !== id
          ),
        }));
      },

      toggleFavorite: (item) => {
        const state = get();
        const id = generateId(item.videoId, item.source);
        const exists = state.favorites.some(
          (fav) => generateId(fav.videoId, fav.source) === id
        );

        if (exists) {
          state.removeFavorite(item.videoId, item.source);
          return false;
        } else {
          state.addFavorite(item);
          return true;
        }
      },

      isFavorite: (videoId, source) => {
        const state = get();
        const id = generateId(videoId, source);
        return state.favorites.some(
          (fav) => generateId(fav.videoId, fav.source) === id
        );
      },

      addToHistory: (item) => {
        const id = generateId(item.videoId, item.source);
        set((state) => {
          const filtered = state.history.filter(
            (h) => generateId(h.videoId, h.source) !== id
          );
          const newHistory = [
            { ...item, watchedAt: Date.now() },
            ...filtered,
          ].slice(0, MAX_HISTORY);
          return { history: newHistory };
        });
      },

      updateHistoryProgress: (videoId, source, progress) => {
        const id = generateId(videoId, source);
        set((state) => ({
          history: state.history.map((h) =>
            generateId(h.videoId, h.source) === id
              ? { ...h, progress, watchedAt: Date.now() }
              : h
          ),
        }));
      },

      removeFromHistory: (videoId, source) => {
        const id = generateId(videoId, source);
        set((state) => ({
          history: state.history.filter(
            (h) => generateId(h.videoId, h.source) !== id
          ),
        }));
      },

      clearHistory: () => set({ history: [] }),
    }),
    {
      name: 'movie-user-storage',
    }
  )
);

interface SearchHistoryState {
  queries: string[];
  addQuery: (query: string) => void;
  removeQuery: (query: string) => void;
  clearQueries: () => void;
}

export const useSearchHistoryStore = create<SearchHistoryState>()(
  persist(
    (set, get) => ({
      queries: [],

      addQuery: (query) => {
        const trimmed = query.trim();
        if (!trimmed) return;

        set((state) => {
          const filtered = state.queries.filter((q) => q !== trimmed);
          return { queries: [trimmed, ...filtered].slice(0, 20) };
        });
      },

      removeQuery: (query) => {
        set((state) => ({
          queries: state.queries.filter((q) => q !== query),
        }));
      },

      clearQueries: () => set({ queries: [] }),
    }),
    {
      name: 'movie-search-history',
    }
  )
);

interface AuthUser {
  id: string;
  username: string;
  email: string;
  avatar?: string;
  role: 'user' | 'admin';
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (user: AuthUser, token: string) => void;
  logout: () => void;
  updateUser: (user: Partial<AuthUser>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      login: (user, token) => set({ user, token, isAuthenticated: true }),
      logout: () => set({ user: null, token: null, isAuthenticated: false }),
      updateUser: (userData) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        })),
    }),
    {
      name: 'movie-auth-storage',
    }
  )
);
